import { getNestedProperty } from '@/helpers/utilsHelper'

export function getMinMaxValues(array) {
	let min = Infinity
	let max = -Infinity

	for (const value of array) {
		if (value < min) {
			min = value
		}
		if (value > max) {
			max = value
		}
	}

	return [min, max]
}

// example keyMap = {oldKey1: 'newKey1', oldKey2:'newKey2', ...} 
export function renameKeys(arr, keyMap) {
	if (!Array.isArray(arr)) return []

	return arr.map(obj => {
		const newObj = {};
		for (let key in obj) {
			key in keyMap ? newObj[keyMap[key]] = obj[key] : newObj[key] = obj[key];
		}
		return newObj;
	})
}

export function compressObjectAsArray(obj, mapping = []) {
	let result = Array(mapping.length).fill(null)
	mapping.forEach(key => {
		result[key.index] = obj[key.key]
	})
	return result
}

export function decompressArrayAsObject(arr, mapping = []) {
	let result = {}
	mapping.forEach(key => {
		result[key.key] = arr[key.index]
	})
	return result
}

/**
 * Returns an array of all unique values for a specific attribute in an array of objects.
 *
 * @param {Array} array - The array of objects.
 * @param {string} attribute - The attribute whose unique values should be retrieved (supports dot notation for nested properties).
 * @returns {Array} - An array of unique attribute values.
 */
export const getUniqueAttributeValues = (array, attribute) => {
	const uniqueValues = new Set()

	for (let index = 0; index < array.length; index++) {
			const element = array[index]
			const value = getNestedProperty(element, attribute)

			if (value !== undefined) {
					uniqueValues.add(value)
			}
	}

	return [...uniqueValues]
}

/**
 * Grouped array by attribute
 * @param {array} array
 * @param {string} attribute
 * @returns {object}
 */
export const groupedArrayByAttribute = (array, attribute) => {
	// console.time('groupedEntitiesByAttribute')
	const groupedEntities = {}
	for (let index = 0; index < array.length; index++) {
		const element = array[index]
		// Use getNestedProperty to get the value of the attribute (supports dot notation)
		const key = getNestedProperty(element, attribute)
		if (groupedEntities[key]) {
			groupedEntities[key].push(element)
		} else {
			groupedEntities[key] = [element]
		}
	}
	// console.timeEnd('groupedEntitiesByAttribute')
	return groupedEntities
}

/**
 * Sorts an array of objects by a specified attribute.
 * @param {Array} array - The array of objects to sort.
 * @param {string} attribute - The attribute to sort by (supports dot notation for nested properties).
 * @param {boolean} [asc=true] - Whether to sort in ascending order (default is true).
 * @returns {Array} - The sorted array.
 */
export const sortArrayByAttribute = (array, attribute, asc = true) => {
	return [...array].sort((a, b) => {
			const valueA = getNestedProperty(a, attribute)
			const valueB = getNestedProperty(b, attribute)

			if (valueA === valueB) return 0

			if (asc) {
					return valueA > valueB ? 1 : -1
			} else {
					return valueA < valueB ? 1 : -1
			}
	})
}

/**
 * Sorts an array of objects by multiple attributes.
 * @param {Array} array - The array of objects to sort.
 * @param {Array<{ attribute: string, asc: boolean }>} attributes - An array of sorting instructions.
 *        Each item is an object with `attribute` (the property to sort by) and `asc` (true for ascending, false for descending).
 * @returns {Array} - The sorted array.
 */
export const sortArrayByMultipleAttributes = (array, attributes) => {
	return [...array].sort((a, b) => {
		for (const { attribute, asc = true } of attributes) {
			const valueA = getNestedProperty(a, attribute)
			const valueB = getNestedProperty(b, attribute)

			if (valueA === valueB) continue // Если значения равны, идём к следующему атрибуту

			if (asc) {
				return valueA > valueB ? 1 : -1
			} else {
				return valueA < valueB ? 1 : -1
			}
		}
		return 0 // Если все атрибуты равны, сохраняем исходный порядок
	})
}

export const haveCommonElements = (array1, array2) => {
	const set1 = new Set(array1)

    for (const element of array2) {
        if (set1.has(element)) {
            return true
        }
    }

    return false
}

/**
 * Counts all unique values of a specific attribute in an array of objects.
 *
 * @param {Array} array - The array of objects.
 * @param {string} attribute - The attribute whose unique values should be counted (supports dot notation for nested properties).
 * @returns {Object} - An object where keys are the unique attribute values and values are their counts.
 */
export const countUniqueAttributeValues = (array, attribute) => {
	const uniqueCounts = {}
	for (let index = 0; index < array.length; index++) {
			const element = array[index]
			const key = getNestedProperty(element, attribute)

			if (uniqueCounts[key]) {
					uniqueCounts[key]++
			} else {
					uniqueCounts[key] = 1
			}
	}
	return uniqueCounts
}

/**
 * Возвращает массив, разбитый на части заданного размера.
 * Например, для массива [1, 2, 3, 4, 5] и размера 2 вернёт [[1, 2], [3, 4], [5]].
 * Если размер не указан, то по умолчанию будет 2.
 * @param {array<any>} array SourceArray
 * @param {number} chunkSize Size of each chunk
 * @returns {array<array<any>>} Array of chunks
 */
export const getArrayOfChunks = (array, chunkSize = 2) => {
	if (!Array.isArray(array) || array.length === 0) return []

	const chunks = []
	for (let i = 0; i < array.length; i += chunkSize) {
		chunks.push(array.slice(i, i + chunkSize))
	}
	return chunks
}
